import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { groupBy } from "lodash";

import {
  Alert,
  Button,
  Divider,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Add, FilterList, ImportExport, RestartAlt } from "@mui/icons-material";

import { AudienceListSkeleton } from "./AudienceListSkeleton";
import { shareAudience, updateAudienceName } from "@/api";
import {
  AudienceCard,
  ButtonIcon,
  CreateNewCard,
  FilterBox,
  SearchBox,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { useAudienceContext, useDialogs } from "@/context";
import { useAudiences } from "@/hooks";
import { Audience, FilterOption } from "@/types";
import { audienceSortOptions, filterAudiences } from "@/utils";

const AudienceListPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { dispatch } = useAudienceContext();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialogs();

  const { audiences, loading, error: audiencesError } = useAudiences("build");

  const [rows, setRows] = useState<Audience[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<Audience | null>(
    null,
  );
  const [searchText, setSearchText] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [filterBy, setFilterBy] = useState<string>("");
  const [projects, setProjects] = useState<FilterOption[]>([]);
  const [updateError, setUpdateError] = useState<string | undefined>(undefined);

  const filterApplied = searchText !== "" || filterBy !== "" || sortBy !== "";

  useEffect(() => {
    setProjects(
      Array.from(
        new Set(
          audiences
            ?.filter((a) => a.project_name !== null)
            .map((a) => a.project_name as string)
            .sort((a, b) => a.localeCompare(b)),
        ),
      ).map((x) => ({ name: x, label: x })),
    );

    const filteredAudiences = filterAudiences(
      audiences,
      searchText,
      filterBy,
      sortBy,
    );

    if (filteredAudiences) setRows(filteredAudiences);
  }, [audiences, searchText, filterBy, sortBy]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSortSelect = (event: SelectChangeEvent) =>
    setSortBy(event.target.value as string);

  const handleFilterBySelect = (value: string) =>
    setFilterBy((prevState) => (prevState === value ? "" : value));

  const handleReset = () => {
    setSearchText("");
    setFilterBy("");
    setSortBy("");
  };

  const handleAudienceCreate = () => {
    dispatch({ type: "CREATE_AUDIENCE" });
    navigate("new");
  };

  const handleAudienceSelect = (audience: Audience) => {
    dispatch({ type: "GET_AUDIENCE", payload: { audience } });
    navigate(audience.audience_id);
  };

  const handleAudienceEdit = (audience: Audience) => {
    setSelectedAudience(audience);
    openDialog({
      type: 'editAudience',
      props: {
        audienceName: audience.audience_name,
        projectName: audience.project_name,
        onConfirm: handleUpdateAudienceName,
      },
    });
  };

  const handleAudienceDelete = (audience: Audience) => {
    setSelectedAudience(audience);
    openDialog({
      type: 'deleteAudience',
      props: {
        audience,
        redirect: '/audience',
      },
    });
  };

  const handleAudienceShare = (audience: Audience) => {
    setSelectedAudience(audience);
    openDialog({
      type: 'shareAudience',
      props: {
        selectedAudience: audience,
        onShare: async (ids: number[]) => {
          await handleShareAudience(audience, ids);
        },
      },
    });
  };

  const handleAudienceInsights = (audience: Audience) => {
    dispatch({ type: "GET_AUDIENCE", payload: { audience } });
    navigate(`${audience.audience_id}/insight`);
  };

  const handleAudienceExport = (audience: Audience) => {
    dispatch({ type: "GET_AUDIENCE", payload: { audience } });
    navigate(`${audience.audience_id}/export`);
  };

  const handleUpdateAudienceName = async (
    audienceName: string,
    projectName: string | null,
  ) => {
    const token = await getAccessTokenSilently();

    if (!selectedAudience) return;

    const { data, error } = await updateAudienceName(
      token,
      selectedAudience.audience_id,
      audienceName,
      projectName,
    );

    if (data) {
      dispatch({
        type: "CHANGE_NAME",
        payload: {
          audienceId: selectedAudience.audience_id,
          audienceName,
          projectName,
        },
      });
      closeDialog();
      setSelectedAudience(null);
    }

    if (error) {
      setUpdateError(error.message);
      closeDialog();
    }
  };

  const handleShareAudience = async (audience: Audience, ids: number[]) => {
    const accessToken = await getAccessTokenSilently();

    // if (!selectedAudience) return;

    const { data, error } = await shareAudience(
      accessToken,
      audience.audience_id || "",
      ids,
    );

    if (data) {
      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
      closeDialog();
      setSelectedAudience(null);
    }

    if (error) {
      closeDialog();
      setUpdateError(error.message);
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Add />}
        onClick={handleAudienceCreate}
      >
        Create New
      </Button>
    </Stack>
  );

  return (
    <PageContainer actions={actionButtons}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography>My Audiences</Typography>
        </Grid>

        {/* Filters & Search bar */}
        <Grid container size={12} alignItems="center">
          <Grid>
            <SearchBox value={searchText} onSearch={handleSearch} />
          </Grid>
          <Grid>
            <FilterBox
              label="Project"
              value={filterBy}
              options={projects}
              onClick={handleFilterBySelect}
              startIcon={<FilterList fontSize="small" />}
              showSelected
            />
          </Grid>
          <Grid>
            <FilterBox
              label="Sort"
              value={sortBy}
              options={audienceSortOptions}
              onChange={handleSortSelect}
              startIcon={<ImportExport fontSize="small" />}
              showSelected
            />
          </Grid>
          <Grid>
            <ButtonIcon
              onClick={handleReset}
              color="info"
              size={38}
              Icon={RestartAlt}
              tooltip="Reset Filters"
            />
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container size={12} mb={4}>
          {loading ? (
            <AudienceListSkeleton />
          ) : audiencesError ? (
            <Grid>
              <Alert variant="filled" severity="error">
                It was not possible to load your audiences at this time, please
                try again.
              </Alert>
            </Grid>
          ) : updateError ? (
            <Grid>
              <Alert variant="filled" severity="error">
                There was an error updating your audience, please try again.
              </Alert>
            </Grid>
          ) : (
            <>
              {/* Create a section for each project */}
              {Object.entries(groupBy(rows, "project_name")).map(
                ([project, projectAudiences], idx) => (
                  <React.Fragment key={idx}>
                    {/* Don't show a header if there's no project unless a filter has been applied */}
                    {(project !== "null" || filterApplied) && (
                      <Grid size={12}>
                        <Divider textAlign="left">
                          <Typography variant="h5">
                            {project !== "null" ? project : ""}
                          </Typography>
                        </Divider>
                      </Grid>
                    )}

                    {/* Create Audience Card - Only show if no filter has been applied */}
                    <Grid container size={12} spacing={2}>
                      {project === "null" && !filterApplied && (
                        <CreateNewCard
                          title="Create New Audience"
                          onClick={handleAudienceCreate}
                        />
                      )}

                      {/* Project Audience Cards */}
                      {projectAudiences.map((audience, index) => (
                        <Grid key={index}>
                          <AudienceCard
                            audience={audience}
                            onSelect={handleAudienceSelect}
                            onDeleteAudience={handleAudienceDelete}
                            onEditAudience={handleAudienceEdit}
                            onShareAudience={handleAudienceShare}
                            onViewInsights={handleAudienceInsights}
                            onViewExport={handleAudienceExport}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </React.Fragment>
                ),
              )}
            </>
          )}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default AudienceListPage;
